import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Minus, Plus } from "lucide-react";
import api from "@/lib/api";
import { addToCart, getBranding, saveBranding } from "@/lib/cart";
import LogoUpload from "@/components/LogoUpload";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(25);
  const [branding, setBranding] = useState(getBranding() || { logo_text: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/products/${slug}`)
      .then((res) => setProduct(res.data))
      .catch(() => setError("Product not found."));
  }, [slug]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!product) return <p>Loading…</p>;

  const handleAdd = () => {
    if (product.customizable) saveBranding(branding);
    addToCart({
      product_id: product.id,
      name: product.name,
      price: product.price,
      quantity,
    });
    navigate("/cart");
  };

  return (
    <div className="grid gap-10 md:grid-cols-2">
      {/* Left — product info */}
      <div>
        {product.badge && (
          <span className="rounded-full bg-fairway-100 px-2.5 py-0.5 text-xs font-semibold text-fairway-700">
            {product.badge}
          </span>
        )}
        <h1 className="mt-2 text-3xl font-extrabold">{product.name}</h1>
        <p className="mt-1 text-gray-600">{product.tagline}</p>
        <p className="mt-4 text-3xl font-extrabold text-fairway-700">
          ${product.price?.toFixed(2)}
          <span className="ml-1 text-sm font-medium text-gray-500">per kit</span>
        </p>
        <p className="mt-4 text-sm leading-relaxed text-gray-700">
          {product.description}
        </p>

        <h3 className="mt-6 font-bold">What's Inside</h3>
        <ul className="mt-2 space-y-1.5">
          {(product.whats_inside || []).map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
              <CheckCircle2 size={16} className="text-fairway-600" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Right — customization + order */}
      <div className="card space-y-6 p-6">
        {product.customizable ? (
          <>
            <div>
              <h2 className="text-lg font-bold">Customize with your logo</h2>
              <p className="text-sm text-gray-500">
                Add branding for your tournament or event.
              </p>
            </div>
            <LogoUpload value={branding} onChange={setBranding} />
          </>
        ) : (
          <p className="text-sm text-gray-600">
            This product is sold as-is and is not customizable.
          </p>
        )}

        <div>
          <label className="label">Quantity</label>
          <div className="flex items-center gap-3">
            <button
              className="btn-outline px-3 py-2"
              onClick={() => setQuantity((q) => Math.max(1, q - 5))}
            >
              <Minus size={16} />
            </button>
            <input
              type="number"
              min="1"
              className="input w-24 text-center"
              value={quantity}
              onChange={(e) =>
                setQuantity(Math.max(1, parseInt(e.target.value || "1", 10)))
              }
            />
            <button
              className="btn-outline px-3 py-2"
              onClick={() => setQuantity((q) => q + 5)}
            >
              <Plus size={16} />
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Most events order 25–100 kits.
          </p>
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <span className="text-sm text-gray-500">Subtotal</span>
          <span className="text-xl font-extrabold">
            ${(product.price * quantity).toFixed(2)}
          </span>
        </div>
        <button className="btn-primary w-full" onClick={handleAdd}>
          Add to Cart
        </button>
      </div>
    </div>
  );
}
